import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/pro-light-svg-icons/faCircleNotch';
import { faHourglassHalf } from '@fortawesome/pro-light-svg-icons/faHourglassHalf';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { useTRPCClientAPI } from '../../../trpc/trpc';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { navigateToReturnUrl, takeCreditPackReturnUrl } from '../promote/credits/creditPackReturn';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_TIME_MS = 30000;
/** Reads that fail in a row, with no answer before, before the page stops. */
const MAX_FAILED_READS = 3;

const messages = defineMessages({
  checkingTitle: {
    id: 'app.sml.payment.complete.creditPack.checkingTitle',
    defaultMessage: 'Adding your video credits...',
  },
  checkingBody: {
    id: 'app.sml.payment.complete.creditPack.checkingBody',
    defaultMessage: 'Then you go back to where you were.',
  },
  pendingTitle: { id: 'app.sml.payment.complete.creditPack.pendingTitle', defaultMessage: 'Payment processing' },
  pendingBody: {
    id: 'app.sml.payment.complete.creditPack.pendingBody',
    defaultMessage: 'The credits come in when the payment is complete. This can take a few minutes.',
  },
  goBack: { id: 'app.sml.payment.complete.creditPack.goBack', defaultMessage: 'Go back' },
  failedTitle: { id: 'app.sml.payment.complete.failed.title', defaultMessage: 'We could not confirm the payment' },
  failedBody: {
    id: 'app.sml.payment.complete.creditPack.failedBody',
    defaultMessage: 'We found no paid checkout for this link. If you paid, the credits come in a few minutes.',
  },
});

/** The answers of `billing.verifyCheckoutSession` that mean the pack is paid. */
function isPaid(paymentStatus: string | null | undefined): boolean {
  return paymentStatus === 'paid' || paymentStatus === 'no_payment_required';
}

interface CreditPackCompleteProps {
  sessionId: string | null;
}

/**
 * The return from a credit-pack Checkout (`/app/paymentComplete?session_id=…&creditPack=1`). The
 * call to `billing.verifyCheckoutSession` grants the pack when the webhook did not yet (the grant
 * is once per session). Then the page goes back to the page the user bought from, with the draft
 * that `saveCreditPackReturn` kept (owner rule 2026-10-06). With no stored page, it opens the
 * Studio. A payment that stays open shows "Payment processing" and "Go back".
 */
const CreditPackCompleteComponent: React.FC<CreditPackCompleteProps> = ({ sessionId }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const trpcClient = useTRPCClientAPI();
  const [isPending, setIsPending] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const goBack = useCallback(() => {
    const returnUrl = takeCreditPackReturnUrl(window.location.origin);
    if (returnUrl !== null) {
      navigateToReturnUrl(returnUrl);
      return;
    }
    router.replace(SMLRoutes.promoteStudio);
  }, [router]);

  useEffect(() => {
    if (sessionId === null) {
      goBack();
      return;
    }
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let elapsed = 0;
    let failedReads = 0;
    let hadAnswer = false;

    const poll = async () => {
      try {
        const session = await trpcClient.billing.verifyCheckoutSession.query({ sessionId });
        if (cancelled) {
          return;
        }
        hadAnswer = true;
        if (isPaid(session.paymentStatus)) {
          goBack();
          return;
        }
      } catch (error) {
        console.error(error);
        // A wrong session id fails every read (QA finding brk-8): stop after a few, do not retry for 30 s.
        failedReads += 1;
        if (failedReads >= MAX_FAILED_READS && !hadAnswer) {
          if (!cancelled) {
            setIsFailed(true);
          }
          return;
        }
      }
      if (cancelled) {
        return;
      }
      elapsed += POLL_INTERVAL_MS;
      if (elapsed >= MAX_POLL_TIME_MS) {
        setIsPending(true);
        return;
      }
      timeoutId = setTimeout(() => {
        poll().catch(console.error);
      }, POLL_INTERVAL_MS);
    };

    poll().catch(console.error);
    return () => {
      cancelled = true;
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [goBack, sessionId, trpcClient]);

  if (isFailed) {
    return (
      <div className="text-center" data-testid="credit-pack-failed">
        <FontAwesomeIcon icon={faHourglassHalf} size="3x" className="mb-4 text-muted" />
        <h2 className="mb-3">{intl.formatMessage(messages.failedTitle)}</h2>
        <p className="text-muted mb-4">{intl.formatMessage(messages.failedBody)}</p>
        <SMLButton onClick={goBack}>{intl.formatMessage(messages.goBack)}</SMLButton>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="text-center" data-testid="credit-pack-pending">
        <FontAwesomeIcon icon={faHourglassHalf} size="3x" className="mb-4 text-warning" />
        <h2 className="mb-3">{intl.formatMessage(messages.pendingTitle)}</h2>
        <p className="text-muted mb-4">{intl.formatMessage(messages.pendingBody)}</p>
        <SMLButton onClick={goBack}>{intl.formatMessage(messages.goBack)}</SMLButton>
      </div>
    );
  }

  return (
    <div className="text-center" data-testid="credit-pack-checking">
      <FontAwesomeIcon icon={faCircleNotch} size="3x" spin className="mb-4 text-primary" />
      <h2 className="mb-3">{intl.formatMessage(messages.checkingTitle)}</h2>
      <p className="text-muted">{intl.formatMessage(messages.checkingBody)}</p>
    </div>
  );
};

export const CreditPackComplete = TypedMemo(CreditPackCompleteComponent);
